import { fontFamily, drawPlaceholder } from "./utils.js";
import { addHeader } from "./headerFooter.js";
import { fetchImageUrl, convertToBase64 } from "./imageService.js";
import darpha from "../../assets/darpha_logo.png";

// Función para dibujar bordes de secciones
const drawSectionBorder = (doc, startY, endY) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginLeft = 13;
  const marginRight = 13;
  const borderWidth = pageWidth - marginLeft - marginRight;

  doc.setDrawColor(204, 0, 0);
  doc.setLineWidth(0.5);
  doc.rect(marginLeft, startY, borderWidth, endY - startY);
};

// Función para dibujar títulos con fondo rojo
const drawRedTitle = (doc, titulo, y, marginLeft = 15) => {
  doc.setFontSize(8);
  doc.setFont(fontFamily, "bold");

  const textWidth = doc.getTextWidth(titulo);
  const paddingX = 2;
  const paddingY = 1;
  const textHeight = 6;

  doc.setFillColor(204, 0, 0);
  doc.rect(
    marginLeft - paddingX,
    y - textHeight + paddingY,
    textWidth + paddingX * 2,
    textHeight + paddingY,
    "F",
  );

  doc.setTextColor(255, 255, 255);
  doc.text(titulo, marginLeft, y);

  doc.setTextColor(0, 0, 0);
  return y + textHeight + paddingY;
};

// Renderizar sección de imágenes de evidencia
export const renderImagenesEvidenciaSection = async (doc, seccion, y) => {
  // Título de la sección
  doc.setFontSize(14);
  doc.setFont(fontFamily, "bold");
  doc.text(seccion.nombreseccion, 10, y);
  y += 10;

  doc.setFont(fontFamily, "normal");
  doc.setFontSize(10);

  // Filtrar solo los items que son imágenes
  const imageItems = seccion.items.filter((item) => item.idtiporespuesta === 4);

  if (!imageItems.length) {
    doc.text("No hay imágenes disponibles en este reporte", 10, y);
    return y + 10;
  }

  // Configuración del grid de imágenes
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;
  const availableWidth = pageWidth - 2 * margin;
  const imagesPerRow = 3; // Cambiado de 2 a 3
  const maxCellWidth = availableWidth / imagesPerRow - 4; // Reducido de 5 a 4mm para mejor ajuste
  const maxImageHeight = 50.8; // 2 pulgadas (50.8 mm)
  const descriptionHeight = 15;
  const cellPadding = 3; // Reducido de 5 a 3mm para aprovechar mejor el espacio

  // Variables para tracking de posición
  let currentY = y;
  let colPosition = 0;
  let currentRowY = currentY;
  let rowMaxHeight = 0;

  // Procesar cada imagen
  for (let i = 0; i < imageItems.length; i++) {
    const item = imageItems[i];
    let imagePath = null;
    let description = "";

    // Extraer la ruta de la imagen y descripción
    if (typeof item.respuesta === "string") {
      try {
        const data = item.respuesta ? JSON.parse(item.respuesta) : null;
        imagePath = data.valorimagen || data.path;
        description = data.descripcion || "Sin descripción";
      } catch (e) {
        imagePath = item.respuesta;
        description = item.descripcion || "Sin descripción";
      }
    } else if (typeof item.respuesta === "object" && item.respuesta !== null) {
      imagePath = item.respuesta.valorimagen || item.respuesta.path;
      description = item.respuesta.descripcion || "Sin descripción";
    }

    // Posición inicial X basada en la columna actual
    const xPos = margin + colPosition * (maxCellWidth + 4); // Reducido de 5 a 4mm

    try {
      if (!imagePath) {
        throw new Error("No image path found");
      }

      // Obtener URL y datos de la imagen
      const url = await fetchImageUrl(imagePath);

      if (!url) {
        throw new Error("Could not fetch image URL");
      }

      const imageBase64 = await convertToBase64(url);
      if (!imageBase64) {
        throw new Error("Could not convert image to base64");
      }

      // Obtener dimensiones reales y calcular proporciones
      const dimensions = await new Promise((resolve) => {
        const img = new Image();

        img.onload = () => {
          // Calcular dimensiones manteniendo proporción
          let imgWidth, imgHeight;
          const aspectRatio = img.width / img.height;

          if (aspectRatio > 1) {
            // Imagen horizontal (más ancha que alta)
            imgWidth = Math.min(maxCellWidth - cellPadding * 2, img.width);
            imgHeight = imgWidth / aspectRatio;

            // Si la altura sigue siendo muy grande, ajustarla
            if (imgHeight > maxImageHeight) {
              imgHeight = maxImageHeight;
              imgWidth = imgHeight * aspectRatio;
            }
          } else {
            // Imagen vertical (más alta que ancha)
            imgHeight = Math.min(maxImageHeight, img.height);
            imgWidth = imgHeight * aspectRatio;

            // Asegurarse que el ancho no exceda el máximo
            if (imgWidth > maxCellWidth - cellPadding * 2) {
              imgWidth = maxCellWidth - cellPadding * 2;
              imgHeight = imgWidth / aspectRatio;
            }
          }

          resolve({ width: imgWidth, height: imgHeight });
        };

        img.onerror = () => {
          console.error("Error loading image for dimensions");
          resolve({ width: maxCellWidth / 2, height: maxImageHeight / 2 });
        };

        img.src = imageBase64;
        setTimeout(() => {
          if (!img.complete) {
            resolve({ width: maxCellWidth / 2, height: maxImageHeight / 2 });
          }
        }, 1000);
      });

      // Dimensiones finales de la imagen
      const imgWidth = dimensions.width;
      const imgHeight = dimensions.height;

      // Dimensiones del contenedor (añadir padding alrededor de la imagen)
      const containerWidth = imgWidth + cellPadding * 2;
      const containerHeight = imgHeight + cellPadding * 2;

      // Centrar en la celda disponible
      const xOffset = (maxCellWidth - containerWidth) / 2;

      // Calcular la altura total del elemento (contenedor + descripción)
      const totalItemHeight = containerHeight + descriptionHeight;

      // Actualizar altura máxima de la fila actual
      rowMaxHeight = Math.max(rowMaxHeight, totalItemHeight);

      // Verificar si necesitamos nueva página
      if (
        currentRowY + totalItemHeight >
        doc.internal.pageSize.getHeight() - 30
      ) {
        doc.addPage();
        addHeader(doc, 10, doc.tipoMantenimiento);
        currentRowY = 50; // Posición después del encabezado
        rowMaxHeight = totalItemHeight;
      }

      // Dibujar contenedor
      doc.setDrawColor(204, 0, 0);
      doc.setLineWidth(0.5);
      doc.rect(xPos + xOffset, currentRowY, containerWidth, containerHeight);

      // Insertar imagen centrada en el contenedor
      doc.addImage(
        imageBase64,
        "JPEG",
        xPos + xOffset + cellPadding,
        currentRowY + cellPadding,
        imgWidth,
        imgHeight,
      );

      // Añadir descripción centrada bajo el contenedor
      doc.setFontSize(8);
      doc.text(
        description.length > 40
          ? description.substring(0, 37) + "..."
          : description,
        xPos + maxCellWidth / 2,
        currentRowY + containerHeight + 8,
        { align: "center" },
      );
      doc.setFontSize(10);

      // Avanzar a la siguiente posición
      colPosition++;

      // Si llegamos al final de la fila, pasar a la siguiente
      if (colPosition >= imagesPerRow) {
        colPosition = 0;
        currentRowY += rowMaxHeight + 10; // Espacio entre filas
        rowMaxHeight = 0;
      }
    } catch (error) {
      console.error("Error procesando imagen:", error);

      // Dibujar placeholder de error
      const placeholderHeight = 40;
      drawPlaceholder(
        doc,
        xPos,
        currentRowY,
        maxCellWidth,
        placeholderHeight,
        "Error al cargar imagen",
      );

      // Añadir descripción bajo el placeholder
      doc.setFontSize(8);
      doc.text(
        description.length > 40
          ? description.substring(0, 37) + "..."
          : description,
        xPos + maxCellWidth / 2,
        currentRowY + placeholderHeight + 8,
        { align: "center" },
      );
      doc.setFontSize(10);

      // Actualizar altura máxima si es necesario
      rowMaxHeight = Math.max(
        rowMaxHeight,
        placeholderHeight + descriptionHeight,
      );

      // Avanzar a la siguiente posición
      colPosition++;

      // Si llegamos al final de la fila, pasar a la siguiente
      if (colPosition >= imagesPerRow) {
        colPosition = 0;
        currentRowY += rowMaxHeight + 10;
        rowMaxHeight = 0;
      }
    }
  }

  // Si hay imágenes en la última fila incompleta, asegurarse de avanzar la posición Y
  if (colPosition > 0) {
    currentRowY += rowMaxHeight + 10;
  }

  return currentRowY;
};

// Renderizar firmas
export const renderFirmasSection = async (doc, report, seccion, y) => {
  const empresa = report.empresaCliente || "DARPHA FIRE SOLUTIONS";
  const empresaUpper = empresa.toUpperCase();
  const reportId = report.id; // Extraer ID del reporte para firmas locales
  const marginLeft = 15;

  const nombreSeccion = seccion.nombreseccion;
  const titulo = nombreSeccion.toUpperCase();

  // Dibujamos el título con fondo rojo
  y = drawRedTitle(doc, titulo, y, marginLeft);

  const startY = y;
  doc.setFont(fontFamily, "normal");
  doc.setFontSize(10);

  // Función helper para manejar firmas (locales o remotas)
  const processFirma = async (firmaPath) => {
    if (!firmaPath) return null;

    // Import dinámico para evitar dependencias circulares
    const { default: DemoSignatureService } =
      await import("../DemoSignatureService.js");

    // Verificar si es una firma local de demo
    if (DemoSignatureService.isLocalDemoSignature(firmaPath)) {
      return DemoSignatureService.getLocalSignatureImage(firmaPath);
    }

    // Si no es firma local, procesar como imagen normal
    const url = await fetchImageUrl(firmaPath);
    if (!url) return null;

    return await convertToBase64(url);
  };

  const {
    inspector,
    supervisor,
    firmainspector,
    firmasupervisor,
    imagenplanta,
  } = report;

  // Medidas para dividir la página en dos columnas
  const pageWidth = doc.internal.pageSize.getWidth();
  const colWidth = (pageWidth - 2 * marginLeft) / 2;
  const col1Center = marginLeft + colWidth / 2;
  const col2Center = marginLeft + colWidth + colWidth / 2;

  // Dimensiones de firmas y logos
  const firmaWidth = 34,
    firmaHeight = 22;
  const firmaSupWidth = 30,
    firmaSupHeight = 20;
  // Logo Darpha con proporción 1.25:1 (ligeramente horizontal pero más balanceado)
  const logoWidth = 34,
    logoHeight = 27; // 34/1.25 ≈ 27
  let maxY = y;

  // =======================
  // INSPECTOR (COLUMNA IZQUIERDA)
  // =======================
  let yInspector = y;
  if (firmainspector) {
    const firmainspectorBase64 = await processFirma(firmainspector);

    if (firmainspectorBase64) {
      doc.text("Inspector:", col1Center, yInspector, { align: "center" });
      doc.addImage(
        firmainspectorBase64,
        "JPEG",
        col1Center - firmaWidth / 2,
        yInspector + 5,
        firmaWidth,
        firmaHeight,
      );

      // Marca de agua del logo
      doc.setGState(new doc.GState({ opacity: 0.13 }));
      doc.addImage(
        darpha,
        "PNG",
        col1Center - logoWidth / 2,
        yInspector + 5,
        logoWidth,
        logoHeight,
      );
      doc.setGState(new doc.GState({ opacity: 1.0 }));

      doc.text("___________________________", col1Center, yInspector + 30, {
        align: "center",
      });
      doc.text(inspector || "Inspector Demo", col1Center, yInspector + 34, {
        align: "center",
      });
      doc.text("DARPHA FIRE SOLUTIONS", col1Center, yInspector + 39, {
        align: "center",
      });
      maxY = Math.max(maxY, yInspector + 44);
    } else {
      // Si no se pudo cargar la firma, mostrar sin firma
      doc.text("Inspector:", col1Center, yInspector, { align: "center" });
      doc.text("Sin firma", col1Center, yInspector + 15, { align: "center" });
      doc.text("___________________________", col1Center, yInspector + 30, {
        align: "center",
      });
      doc.text("DARPHA FIRE SOLUTIONS", col1Center, yInspector + 39, {
        align: "center",
      });
      maxY = Math.max(maxY, yInspector + 44);
    }
  } else {
    doc.text("Inspector:", col1Center, yInspector, { align: "center" });
    doc.text("Sin firma", col1Center, yInspector + 15, { align: "center" });
    doc.text("___________________________", col1Center, yInspector + 30, {
      align: "center",
    });
    doc.text("DARPHA FIRE SOLUTIONS", col1Center, yInspector + 39, {
      align: "center",
    });
    maxY = Math.max(maxY, yInspector + 44);
  }

  // =======================
  // SUPERVISOR (COLUMNA DERECHA)
  // =======================
  let ySupervisor = y;

  // Obtener imagen de planta (con fallback al logo DARPHA si no está disponible)
  let imagenplantaBase64 = null;
  if (imagenplanta) {
    try {
      const imagenplantaUrl = await fetchImageUrl(imagenplanta);
      if (imagenplantaUrl) {
        imagenplantaBase64 = await convertToBase64(imagenplantaUrl);
      }
    } catch (err) {
      console.warn(
        "No se pudo cargar imagen de planta, usando logo DARPHA:",
        err,
      );
    }
  }
  // Usar logo DARPHA como fallback
  const logoPlanta = imagenplantaBase64 || darpha;

  if (firmasupervisor) {
    const firmasupervisorBase64 = await processFirma(firmasupervisor, reportId);

    doc.text("Supervisor:", col2Center, ySupervisor, { align: "center" });

    // Logo con opacidad
    doc.setGState(new doc.GState({ opacity: 0.13 }));
    doc.addImage(
      logoPlanta,
      "PNG",
      col2Center - logoWidth / 2,
      ySupervisor + 5,
      logoWidth,
      logoHeight,
    );
    doc.setGState(new doc.GState({ opacity: 1.0 }));

    // Firma del supervisor (solo si se cargó correctamente)
    if (firmasupervisorBase64) {
      doc.addImage(
        firmasupervisorBase64,
        "JPEG",
        col2Center - firmaSupWidth / 2,
        ySupervisor + 5,
        firmaSupWidth,
        firmaSupHeight,
      );
    }

    doc.text("___________________________", col2Center, ySupervisor + 30, {
      align: "center",
    });
    doc.text(supervisor || "Supervisor Demo", col2Center, ySupervisor + 34, {
      align: "center",
    });
    doc.text(empresaUpper, col2Center, ySupervisor + 39, { align: "center" });
    maxY = Math.max(maxY, ySupervisor + 44);
  } else {
    doc.text("Supervisor:", col2Center, ySupervisor, { align: "center" });
    doc.setGState(new doc.GState({ opacity: 0.13 }));
    doc.addImage(
      logoPlanta,
      "PNG",
      col2Center - logoWidth / 2,
      ySupervisor + 5,
      logoWidth,
      logoHeight,
    );
    doc.setGState(new doc.GState({ opacity: 1.0 }));
    doc.text("Sin firma", col2Center, ySupervisor + 15, { align: "center" });
    doc.text("___________________________", col2Center, ySupervisor + 30, {
      align: "center",
    });
    doc.text(empresaUpper, col2Center, ySupervisor + 39, { align: "center" });
    maxY = Math.max(maxY, ySupervisor + 44);
  }

  // =======================
  // DIBUJAMOS EL BORDE DE LA SECCIÓN
  // =======================
  drawSectionBorder(doc, startY - 4, maxY + 5);

  return maxY + 15;
};

// Función para renderizar la sección Matriz Causa y Efecto
export const renderMatrizCausaEfectoSection = async (doc, seccion, y) => {
  // Título de la sección
  doc.setFontSize(14);
  doc.setFont(fontFamily, "bold");
  doc.text(seccion.nombreseccion, 10, y);
  y += 10;

  doc.setFont(fontFamily, "normal");
  doc.setFontSize(10);

  // Si no hay máquinas con matrices, mostrar un mensaje
  if (!seccion.maquinas || seccion.maquinas.length === 0) {
    doc.text("No hay matrices causa-efecto disponibles", 10, y);
    return y + 10;
  }

  // Configuración de medidas para las imágenes
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20; // 10mm a cada lado
  const availableWidth = pageWidth - margin;
  const padding = 5;
  const headerHeight = 20; // Espacio para información de la máquina
  const spacing = 15; // Espacio entre matrices

  // Para cada máquina con matriz causa-efecto
  for (const maquina of seccion.maquinas) {
    if (!maquina.matrizcausaefecto) continue;

    // Verificar si hay suficiente espacio en la página actual
    const estimatedHeight = 100; // Altura estimada inicial para comprobar espacio
    if (
      y + headerHeight + estimatedHeight + spacing >
      doc.internal.pageSize.getHeight() - 30
    ) {
      doc.addPage();
      addHeader(doc, 10, doc.tipoMantenimiento);
      y = 50; // Posición después del encabezado
    }

    try {
      // Información de la máquina
      doc.setFillColor(245, 245, 245);
      doc.rect(10, y, availableWidth, headerHeight, "F");

      doc.setFont(fontFamily, "bold");
      doc.text(`Máquina: ${maquina.tipo} - ${maquina.numero}`, 15, y + 8);

      doc.setFont(fontFamily, "normal");
      doc.text(
        `Ubicación: ${maquina.ubicacion || "N/A"}`,
        pageWidth / 2,
        y + 8,
      );

      y += headerHeight + 5;

      // Obtener la imagen de la matriz
      const url = await fetchImageUrl(maquina.matrizcausaefecto);
      if (!url) {
        doc.setFillColor(240, 240, 240);
        doc.rect(10, y, availableWidth, 40, "F");
        doc.text(
          "No se pudo cargar la imagen de la matriz",
          pageWidth / 2,
          y + 20,
          { align: "center" },
        );
        y += 50;
        continue;
      }

      const imageBase64 = await convertToBase64(url);
      if (!imageBase64) {
        doc.setFillColor(240, 240, 240);
        doc.rect(10, y, availableWidth, 40, "F");
        doc.text("Error al procesar la imagen", pageWidth / 2, y + 20, {
          align: "center",
        });
        y += 50;
        continue;
      }

      // Calcular dimensiones proporcionales para la imagen
      const dimensions = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const aspectRatio = img.width / img.height;
          const imgWidth = availableWidth - padding * 2;
          const imgHeight = imgWidth / aspectRatio;
          resolve({ width: imgWidth, height: imgHeight });
        };
        img.onerror = () => {
          console.error("Error cargando la imagen de matriz causa-efecto");
          resolve({ width: availableWidth - padding * 2, height: 100 });
        };
        img.src = imageBase64;
      });

      // Si la imagen es muy alta, agregar una nueva página
      const maxMatrixHeight = doc.internal.pageSize.getHeight() - 30 - 50 - padding * 2;
      if (dimensions.height > maxMatrixHeight) {
        dimensions.width *= maxMatrixHeight / dimensions.height;
        dimensions.height = maxMatrixHeight;
      }
      if (
        y + dimensions.height + padding * 2 >
        doc.internal.pageSize.getHeight() - 30
      ) {
        doc.addPage();
        addHeader(doc, 10, doc.tipoMantenimiento);
        y = 50;
      }

      // Dibujar un borde alrededor de la imagen
      doc.setDrawColor(204, 0, 0);
      doc.setLineWidth(0.5);
      doc.rect(10, y, availableWidth, dimensions.height + padding * 2);

      // Insertar la imagen
      doc.addImage(
        imageBase64,
        "JPEG",
        10 + padding,
        y + padding,
        dimensions.width,
        dimensions.height,
      );

      y += dimensions.height + padding * 2 + spacing;
    } catch (error) {
      console.error("Error procesando matriz causa-efecto:", error);
      doc.setFillColor(240, 240, 240);
      doc.rect(10, y, availableWidth, 40, "F");
      doc.text(
        "Error al procesar la matriz causa-efecto",
        pageWidth / 2,
        y + 20,
        { align: "center" },
      );
      y += 60;
    }
  }

  return y;
};
